import { useState } from 'react'

import { Button, Field, Input, Select, Sheet } from '@/shared/components/ui'
import { useT } from '@/shared/i18n'
import type { CompanyListItem, Role } from '@/shared/types/domain'

import { ROLE_HINT_KEY, ROLE_LABEL_KEY, type UserInviteInput } from '../types'

export interface InviteUserFormProps {
  companies: readonly CompanyListItem[]
  pending?: boolean
  onInvite: (input: UserInviteInput) => void
  onClose: () => void
}

/**
 * Invitar por email.
 *
 * Crea la fila que el primer login va a reclamar: **que alguien tenga cuenta de
 * Google no lo mete al sistema.** Un usuario que existe en Firebase pero no en
 * `users` recibe 403, no auto-provisioning.
 *
 * Un `client` necesita empresa sí o sí (ve exactamente una y la base lo exige
 * con un CHECK), así que el formulario la pide antes de dejar invitar en vez de
 * mandar el request y mostrar el 422.
 *
 * **Se abre desde el botón "Invitar", no está siempre desplegado.** Era una
 * barra de cuatro campos encima de la lista: el botón quedaba al final, y para
 * llegar a él había que completar todo de izquierda a derecha sin que nada
 * dijera que eso era un alta. Ahora la pantalla muestra la lista, y el alta
 * empieza donde uno la va a buscar.
 */
export function InviteUserForm({ companies, pending, onInvite, onClose }: InviteUserFormProps) {
  const t = useT()
  const [email, setEmail] = useState('')
  const [fullName, setFullName] = useState('')
  const [role, setRole] = useState<Role>('tech_member')
  const [companyId, setCompanyId] = useState('')

  const needsCompany = role === 'client'
  const valid = email.trim() !== '' && fullName.trim() !== '' && (!needsCompany || companyId !== '')

  const submit = () => {
    if (!valid) return
    onInvite({
      email: email.trim(),
      display_name: fullName.trim(),
      role,
      ...(needsCompany
        ? { client_company_id: companyId }
        : { company_ids: companyId === '' ? [] : [companyId] }),
    })
    setEmail('')
    setFullName('')
    setCompanyId('')
    onClose()
  }

  return (
    <Sheet
      open
      onClose={onClose}
      title={<h2 className="text-base text-text-1">{t('settings.invite.title')}</h2>}
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button onClick={onClose}>{t('common.action.cancel')}</Button>
          {/* "Enviar invitación" y no "Invitar": el botón que ABRE este panel
              ya se llama así, y dos controles con el mismo nombre en pantalla
              obligan a adivinar cuál hace qué. */}
          <Button variant="primary" disabled={!valid || pending} onClick={submit}>
            {t('settings.invite.confirm')}
          </Button>
        </div>
      }
    >
      <form
        className="flex flex-col gap-3"
        onSubmit={(event) => {
          event.preventDefault()
          submit()
        }}
      >
        <Field label={t('common.label.email')} htmlFor="invite-email">
          <Input
            id="invite-email"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder={t('settings.invite.emailPlaceholder')}
          />
        </Field>

        <Field label={t('common.label.name')} htmlFor="invite-name">
          <Input
            id="invite-name"
            value={fullName}
            onChange={(event) => setFullName(event.target.value)}
            placeholder={t('settings.invite.namePlaceholder')}
          />
        </Field>

        <Field label={t('common.label.role')} htmlFor="invite-role" hint={t(ROLE_HINT_KEY[role])}>
          <Select
            id="invite-role"
            value={role}
            onChange={(event) => setRole(event.target.value as Role)}
          >
            {(Object.keys(ROLE_LABEL_KEY) as Role[]).map((value) => (
              <option key={value} value={value}>
                {t(ROLE_LABEL_KEY[value])}
              </option>
            ))}
          </Select>
        </Field>

        <Field
          label={
            needsCompany
              ? t('settings.invite.companyRequired')
              : t('settings.invite.companyInitial')
          }
          htmlFor="invite-company"
        >
          <Select
            id="invite-company"
            value={companyId}
            onChange={(event) => setCompanyId(event.target.value)}
          >
            <option value="">
              {needsCompany ? t('settings.invite.pickCompany') : t('settings.invite.noCompanyYet')}
            </option>
            {companies.map((company) => (
              <option key={company.id} value={company.id}>
                {company.name}
              </option>
            ))}
          </Select>
        </Field>
      </form>
    </Sheet>
  )
}
